import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useMemo, useState } from "react";
import { useViewport } from "src/contexts";
import { ImageDisplay, StepWrapper, SummaryCardList } from "src/pages/configurator/components";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { getSelectedStructuralOptionCodes, Icon } from "~components";
import { Css } from "~generated/css";
import { removePilotPrefix, useEscCapture } from "~utils";
import { getCCFGImages } from "../Configurator.utils";
import { FlowStep } from "../stepTemplates/FlowSteps";
import { SummaryCards_CheckoutConfigFragment } from "./SummaryCards.generated";

type SummaryDrawerProps = {
  onClose: () => void;
  isOpen: boolean;
  bottomOffset?: number;
};

const variants = {
  open: (bottomOffset: number) => ({ height: `calc(100% - ${bottomOffset}px)` }),
  // NOTE: If we ever add a transition time here, we need to update `Interior.step.tsx` timeout to use the same transition time.
  closed: { height: 0 },
  exit: { height: 0 },
};

export function SummaryDrawer(props: SummaryDrawerProps) {
  const { onClose, isOpen, bottomOffset = 64 } = props;
  const { goToStep, checkoutConfig } = useConfiguratorContext();
  const { isMobile } = useViewport();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const images = useMemo(() => getCCFGImages(checkoutConfig), [checkoutConfig]);

  // Compute selected structural option codes for floor plan highlighting
  const selectedStructuralOptionCodes = useMemo(() => {
    const optionGroups = checkoutConfig?.optionGroups ?? [];
    return getSelectedStructuralOptionCodes(
      optionGroups,
      optionGroups.flatMap((og) => og.selectedOption?.id).compact(),
    );
  }, [checkoutConfig?.optionGroups]);

  // Hide images first to remove expensive SVG from DOM that ruins the closing animation
  // Wait a frame for ImageDIsplay to unmount, then close drawer
  const handleClose = useCallback(() => {
    setDrawerOpened(false);
    requestAnimationFrame(() => {
      onClose();
    });
  }, [onClose]);

  const drawerGoToStep = useCallback(
    (step: FlowStep) => {
      goToStep(step);
      handleClose();
    },
    [handleClose, goToStep],
  );

  useEscCapture(handleClose);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="summary-drawer"
          id="summary-drawer"
          initial="closed"
          animate="open"
          exit="exit"
          variants={variants}
          custom={bottomOffset}
          // z3 is our highest non overlay z-index
          css={Css.fixed.bgGray800.z1.bottomPx(bottomOffset).w100.ifMobile.oa.bgWhite.z3.$}
          onAnimationComplete={(definition) => {
            setDrawerOpened(definition === "open");
          }}
        >
          {/* Content Container */}
          <div css={Css.df.fdc.gap1.h100.oa.ifTabletAndUp.ma.oh.$}>
            {/* Header */}
            <div css={Css.w100.df.aic.jcsb.h8.px3.ifMobile.pt2.pb1.$}>
              {/* Your Home */}
              <div css={Css.t24.ifTabletAndUp.white.$}>{removePilotPrefix(checkoutConfig?.name) ?? "Your Home"}</div>
              {/* Close Button */}
              <div onClick={handleClose}>
                <Icon name={isMobile ? "x" : "x-white"} />
              </div>
            </div>
            {/* The mobile version reuses the Summary.step.tsx layout without extra functionality and config status info.
                We can reuse StepWrapper since mobile doesn't use dark mode */}
            {isMobile ? (
              <StepWrapper
                inSummaryDrawer
                loading={!checkoutConfig}
                loadingView="accordion"
                title={""}
                description={""}
                left={
                  drawerOpened ? (
                    <ImageDisplay images={images} selectedOptionCodes={selectedStructuralOptionCodes} />
                  ) : (
                    <TransparentBlock />
                  )
                }
                right={checkoutConfig && <SummaryCardList checkoutConfig={checkoutConfig} goToStep={drawerGoToStep} />}
              />
            ) : (
              <>
                <div css={Css.df.h100.w100.oh.gap3.$}>
                  {/* Left */}
                  <div css={Css.relative.oh.z0.df.fdc.ifXlAndDown.w("68.75%").else.w("75%").$}>
                    {/* Don't show any images until the drawer is opened to keep it smooth. 
                      Showing our exterior renderings resize while opening isn't pretty. 
                      The extra iframes loading is also adding some lag. */}
                    {drawerOpened ? (
                      <ImageDisplay images={images} contrast selectedOptionCodes={selectedStructuralOptionCodes} />
                    ) : (
                      <TransparentBlock />
                    )}
                  </div>
                  {/* Right */}
                  <div
                    css={{
                      ...Css.relative.oa.z1.df.fdc
                        .mwPx(400)
                        .ifXlAndDown.w("31.25%")
                        .else.w("25%")
                        .gap1.oya.bc("rgba(252, 252, 250, 0.10)")
                        .ba.br4.addIn("> div:not(:last-child)", Css.br0.bb.bc("rgba(252, 252, 250, 0.10)").$).$,
                      // TODO: Add dark mode scrollbar to truss / config
                      ...Css.addIn("::-webkit-scrollbar", { ...Css.wPx(10).$ }).$,
                      ...Css.addIn("::-webkit-scrollbar-thumb", { ...Css.bgGray400.br24.$ }).$,
                    }}
                  >
                    {checkoutConfig && (
                      <SummaryCardList
                        checkoutConfig={checkoutConfig as SummaryCards_CheckoutConfigFragment}
                        goToStep={drawerGoToStep}
                        contrast
                      />
                    )}
                  </div>
                </div>
                {/* Decorative shadow over stepper */}
                <div
                  css={
                    Css.w100.absolute
                      .bottomPx(-12)
                      .bgColor("inherit")
                      .hPx(12)
                      .add(
                        "filter",
                        "drop-shadow(-4px -4px 12px rgba(254, 254, 254, 0.1)) drop-shadow(4px 4px 12px rgba(254, 254, 254, 0.1));",
                      ).$
                  }
                />
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function TransparentBlock() {
  return <div css={Css.w100.h100.bgTransparent.$} />;
}
